import addUserSuggestionFragment from 'Root/App/Contexts/Authorizations/SlideContainer/AddUser/Suggestion/add-user-suggestion.graphql'
import fetchElementAuthorizationsAuthorizationFragment from 'Root/App/Contexts/Authorizations/SlideContainer/Row/Authorization/fetch-element-authorizations-authorization.graphql'
import fetchElementAuthorizationsInviteFragment from 'Root/App/Contexts/Authorizations/SlideContainer/Row/Invite/fetch-element-authorizations-invite.graphql'
import userProfileFragment from 'Root/App/Item/UserProfile/user-profile.graphql'

import { useEffect, useState } from 'react'

import { filter, map } from 'lodash'
import InfiniteScroll from 'react-infinite-scroll-component'

import { gql, useQuery } from '@apollo/client'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Title from 'Root/App/Title'
import FetchMoreLoading from 'Root/Pages/Homepage/App/FetchMoreLoading'
import { useCurrentUserId } from 'lib/use-current-user-id'

import AddUser from './AddUser'
import Loading from './Loading'
import Authorization from './Row/Authorization'
import Invite from './Row/Invite'
import ShareSectionHeading from './share-section-heading'

const ITEMS_PER_PAGE = 25
const SEARCH_DEBOUNCE_MS = 300

const GET_ELEMENT_AUTHORIZATION_FOR_USERS = gql`
  ${userProfileFragment}
  ${addUserSuggestionFragment}
  ${fetchElementAuthorizationsAuthorizationFragment}
  ${fetchElementAuthorizationsInviteFragment}
  fragment PaginatedAuthorizationsFields on PaginatedAuthorizationsAndInvites {
    data {
      __typename
      ... on Authorization {
        ...FetchElementAuthorizationsAuthorization
      }
      ... on Invite {
        ...FetchElementAuthorizationsInvite
      }
    }
    paginatorInfo {
      currentPage
      lastPage
      perPage
      total
      count
      hasMorePages
    }
  }

  query GetElementAuthorizationForUsers(
    $id: ID!
    $page: Int = 1
    $first: Int = 25
    $search: String
  ) {
    user: me {
      emails {
        email
        id
        primary
        verifiedAt
      }
      id
    }

    element(id: $id) {
      __typename

      ... on Collection {
        id
        paginatedAuthorizations: paginatedAuthorizationsAndInvites(
          page: $page
          first: $first
          search: $search
        ) {
          ...PaginatedAuthorizationsFields
        }
        creator {
          ...UserProfile
          email
        }
      }

      ... on Content {
        id
        paginatedAuthorizations: paginatedAuthorizationsAndInvites(
          page: $page
          first: $first
          search: $search
        ) {
          ...PaginatedAuthorizationsFields
        }
        creator {
          ...UserProfile
          email
        }
      }

      ... on Folder {
        id
        paginatedAuthorizations: paginatedAuthorizationsAndInvites(
          page: $page
          first: $first
          search: $search
        ) {
          ...PaginatedAuthorizationsFields
        }
        creator {
          ...UserProfile
          email
        }
      }
    }

    knownUsers {
      ...AddUserSuggestion
    }
  }
`

const ShareToUser = ({ id }) => {
  const userId = useCurrentUserId()
  const { _ } = useLingui()
  const [allItems, setAllItems] = useState<any[]>([])
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMoreData, setHasMoreData] = useState(true)
  const [isSearching, setIsSearching] = useState(false)

  useEffect(() => {
    if (searchInput === debouncedSearch) {
      setIsSearching(false)
      return
    }
    setIsSearching(true)
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
      setIsSearching(false)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput, debouncedSearch])

  useEffect(() => {
    setCurrentPage(1)
    setHasMoreData(true)
  }, [debouncedSearch])

  const { data, loading, fetchMore, previousData } = useQuery(
    GET_ELEMENT_AUTHORIZATION_FOR_USERS,
    {
      variables: {
        id,
        page: 1,
        first: ITEMS_PER_PAGE,
        search: debouncedSearch || undefined,
      },
      fetchPolicy: 'network-only',
    }
  )

  const effectiveData = data || previousData

  useEffect(() => {
    if (
      loading ||
      currentPage !== 1 ||
      !data ||
      !data.element ||
      !data.element.paginatedAuthorizations
    )
      return

    const { data: pageData, paginatorInfo } =
      data.element.paginatedAuthorizations
    const filteredItems = filter(
      pageData,
      ({ __typename, ...item }) =>
        __typename === 'Invite' || !('user' in item) || item.user.id !== userId
    )

    setAllItems(filteredItems)
    setHasMoreData(paginatorInfo.hasMorePages)
  }, [loading, data, currentPage, userId])

  const loadMore = async () => {
    if (!hasMoreData) return
    const nextPage = currentPage + 1
    const result = await fetchMore({
      variables: {
        id,
        page: nextPage,
        first: ITEMS_PER_PAGE,
        search: debouncedSearch || undefined,
      },
    })
    const conn = result.data?.element?.paginatedAuthorizations
    if (conn) {
      setAllItems((prev) => [...prev, ...conn.data])
      setHasMoreData(conn.paginatorInfo.hasMorePages)
      setCurrentPage(nextPage)
    }
  }

  if (loading && !data && !previousData) {
    return <Loading />
  }

  if (!effectiveData || !effectiveData.element) {
    return null
  }

  const element = effectiveData.element
  const isLoadingSearch = loading && currentPage === 1

  return (
    <span id="share-by-email" className="flex flex-col gap-4">
      <ShareSectionHeading
        title={<Trans>Condividi tramite email</Trans>}
        subtitle={
          <Trans>
            Manderemo alle persone che inviti un link magico e le istruzioni per
            accedere.
          </Trans>
        }
      />
      <AddUser
        id={element.id}
        knownUsers={effectiveData.knownUsers}
        creator={element.creator}
        currentUserData={effectiveData.user.emails}
      />

      <div>
        <Title size="XS">
          <Trans>Condiviso con</Trans>:
          <hr className="mt-4 mb-4 divide-gray-200" />
        </Title>

        <div className="flex items-center gap-2 p-2 border-2 border-gray-200 rounded-lg focus-within:border-brand-main">
          <FontAwesomeIcon icon={faSearch} className="text-gray-900" />
          <input
            type="text"
            value={searchInput}
            placeholder={_(msg`Cerca utente o email...`)}
            className="w-full h-full border-none bg-transparent p-0 m-0 outline-none"
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {isSearching || isLoadingSearch ? (
        <div className="flex justify-center py-8">
          <FetchMoreLoading />
        </div>
      ) : (
        <>
          <InfiniteScroll
            dataLength={allItems.length}
            next={loadMore}
            hasMore={hasMoreData && !isSearching}
            loader={<FetchMoreLoading />}
            scrollableTarget="slide-container"
          >
            <div role="list" className="flex flex-col divide-y divide-gray-200">
              {map(allItems, (item) =>
                item.__typename === 'Authorization' ? (
                  <Authorization
                    authorization={item}
                    elementId={element.id}
                    key={item.id}
                  />
                ) : (
                  <Invite elementId={element.id} invite={item} key={item.id} />
                )
              )}
            </div>
          </InfiniteScroll>

          {allItems.length === 0 &&
            !loading &&
            !isSearching &&
            debouncedSearch && (
              <div className="text-center text-gray-500 mt-5 pb-4">
                <Trans>Nessun utente trovato</Trans>
              </div>
            )}
        </>
      )}

      {!hasMoreData && allItems.length > 0 && (
        <div className="text-center text-gray-500 mt-5 pb-4">
          <Trans>Tutti i risultati sono stati caricati</Trans>
        </div>
      )}
    </span>
  )
}

export default ShareToUser
